import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useOrganizationTheme } from "./hooks/useOrganizationTheme";
import { useBackgroundEmailSync } from "./hooks/useBackgroundEmailSync";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import OnboardingProcessing from "./pages/OnboardingProcessing";
import Archive from "./pages/Archive";
import ContentEditor from "./pages/ContentEditor";
import Subscription from "./pages/Subscription";
import OnboardingProfile from "./pages/OnboardingProfile";
import Welcome from "./pages/Welcome";
import Performance from "./pages/Performance";
import CustomerManagement from "./pages/CustomerManagement";
import Profile from "./pages/Profile";
import OrganizationManagement from "./pages/OrganizationManagement";
import Email from "./pages/Email";
import RunningList from "./pages/RunningList";
import TodoManagement from "./pages/TodoManagement";
import CustomerConfirmation from "./pages/CustomerConfirmation";
import CustomerAppointment from "./pages/CustomerAppointment";
import EmailConfirmation from "./pages/EmailConfirmation";
import NotFound from "./pages/NotFound";
import TeamManagement from "./pages/TeamManagement";
import TeamInvitation from "./pages/TeamInvitation";

const queryClient = new QueryClient();

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  console.log('🛡️ ProtectedRoute: Starting render');
  
  const { user, loading } = useAuth();
  const { loading: themeLoading } = useOrganizationTheme();
  const [onboardingComplete, setOnboardingComplete] = React.useState<boolean | null>(null);
  const [userProfile, setUserProfile] = React.useState<any>(null);
  
  // Global background email sync - runs whenever user is logged in
  useBackgroundEmailSync(user?.id);

  console.log('🛡️ ProtectedRoute: State check - user:', !!user, 'loading:', loading, 'themeLoading:', themeLoading, 'onboardingComplete:', onboardingComplete);

  React.useEffect(() => {
    console.log('🔄 ProtectedRoute: useEffect triggered - user:', !!user);
    
    if (!user) {
      console.log('❌ ProtectedRoute: No user, resetting state');
      setOnboardingComplete(null);
      setUserProfile(null);
      return;
    }

    console.log('ProtectedRoute: Checking onboarding for user:', user.id);

    // Check if user completed onboarding and get profile data
    const checkOnboarding = async () => {
      try {
        console.log('🔍 ProtectedRoute: Fetching profile data...');
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed, organization_id')
          .eq('user_id', user.id)
          .single();
        
        console.log('ProtectedRoute: Profile data fetched:', data, 'Error:', error);
        
        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
        }
        
        setUserProfile(data);
        setOnboardingComplete(data?.onboarding_completed || false);
        console.log('ProtectedRoute: Onboarding complete set to:', data?.onboarding_completed || false);
      } catch (err) {
        console.error('Unexpected error in checkOnboarding:', err);
      }
    };
    
    checkOnboarding();

    // Set up realtime subscription to listen for profile updates
    const subscription = supabase
      .channel('profile_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('ProtectedRoute: Profile updated via realtime:', payload.new);
          setUserProfile(payload.new);
          setOnboardingComplete(payload.new?.onboarding_completed || false);
          console.log('ProtectedRoute: Onboarding complete updated to:', payload.new?.onboarding_completed || false);
        }
      )
      .subscribe((status) => {
        console.log('ProtectedRoute: Realtime subscription status:', status);
      });

    return () => {
      console.log('ProtectedRoute: Unsubscribing from realtime');
      subscription.unsubscribe();
    };
  }, [user]);

  if (loading || themeLoading || (user && onboardingComplete === null)) {
    console.log('⏳ ProtectedRoute: Showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    console.log('🔒 ProtectedRoute: No user, redirecting to Auth');
    return <Navigate to="/auth" replace />;
  }

  // If user exists but onboarding is not complete, redirect to onboarding
  // Allow access to onboarding, onboarding/profile, and welcome pages during onboarding
  if (user && !onboardingComplete) {
    const currentPath = window.location.pathname;
    const allowedPaths = ['/onboarding', '/onboarding/processing', '/onboarding/profile', '/welcome'];
    
    console.log('ProtectedRoute: User has incomplete onboarding. Current path:', currentPath, 'Onboarding complete:', onboardingComplete);
    
    if (!allowedPaths.includes(currentPath)) {
      console.log('ProtectedRoute: Redirecting to onboarding');
      // Start with organization setup instead of profile edit
      return <Onboarding />;
    }
  }

  console.log('✅ ProtectedRoute: Rendering protected content');
  return <>{children}</>;
};

const App = () => {
  console.log('🎯 App.tsx: App component rendering');
  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner duration={3000} />
        <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            } />
            <Route path="/onboarding/processing" element={
              <ProtectedRoute>
                <OnboardingProcessing />
              </ProtectedRoute>
            } />
            <Route path="/onboarding/profile" element={
              <ProtectedRoute>
                <OnboardingProfile />
              </ProtectedRoute>
            } />
            <Route path="/welcome" element={
              <ProtectedRoute>
                <Welcome />
              </ProtectedRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute>
                <Index />
              </ProtectedRoute>
            } />
            <Route path="/archive" element={
              <ProtectedRoute>
                <Archive />
              </ProtectedRoute>
            } />
            <Route path="/editor" element={
              <ProtectedRoute>
                <ContentEditor />
              </ProtectedRoute>
            } />
            <Route path="/subscription" element={
              <ProtectedRoute>
                <Subscription />
              </ProtectedRoute>
            } />
            <Route path="/running-list" element={
              <ProtectedRoute>
                <RunningList />
              </ProtectedRoute>
            } />
            <Route path="/customers" element={
              <ProtectedRoute>
                <CustomerManagement />
              </ProtectedRoute>
            } />
            <Route path="/performance" element={
              <ProtectedRoute>
                <Performance />
              </ProtectedRoute>
            } />
            <Route path="/email" element={
              <ProtectedRoute>
                <Email />
              </ProtectedRoute>
            } />
            <Route path="/email/*" element={
              <ProtectedRoute>
                <Email />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/organization" element={
              <ProtectedRoute>
                <OrganizationManagement />
              </ProtectedRoute>
            } />
            <Route path="/todos" element={
              <ProtectedRoute>
                <TodoManagement />
              </ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute>
                <TeamManagement />
              </ProtectedRoute>
            } />
            <Route path="/customer-confirmation" element={<CustomerConfirmation />} />
            <Route path="/appointment" element={<CustomerAppointment />} />
            <Route path="/customer-appointment" element={<CustomerAppointment />} />
            <Route path="/email-confirmation" element={<EmailConfirmation />} />
            <Route path="/team-invitation" element={<TeamInvitation />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
